"use client";

import { useEffect, useRef, useState } from "react";
import {
  AnimatedTabs,
  AnimatedTabsList,
  AnimatedTabsTrigger,
} from "@/components/ui/animated-tabs";
import {
  useCurrentUser,
  useCurrentOrganization,
  useMembers,
  useMyMemberships,
  useApiKeys,
  useTaxExemption,
  useSubscriptions,
} from "@/hooks/spacetime";
import { isOrgAdminOrOwner } from "@/lib/org-roles";
import {
  getPaymentMethodByPriceId,
  getProductNameByProduct,
  getSubscriptionMetadata,
  getSubscriptionProductIdByProduct,
  PaymentMethod,
  PaymentMethods,
  Product,
  Subscription,
  SubscriptionCollectionMethod,
  SubscriptionCustomer,
} from "@/types/subscription";
import { MsgQuestion } from "@/components/msg-question";

import PowerOnPro from "@/components/icons/poweron-pro";
import PowerOnPipelines from "@/components/icons/poweron-pipelines";
import SubscriptionPowerOnPro from "@/components/subscription-poweron-pro";
import SubscriptionPowerOnPipelines from "@/components/subscription-poweron-pipelines";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  DEFAULT_DEBOUNCE_DELAY,
  DEFAULT_ERROR_TITLE,
  DEFAULT_MISSING_PERMISSION_TITLE,
} from "@/lib/constants";
import {
  createCustomer,
  getCustomerFromSessionID,
  matchCustomerToOrganization,
} from "@/services/customer.client";
import { formatLongDate, overrideMessageIf403 } from "@/utils/utils";
import { useDebounce } from "@/hooks/useDebounce";
import {
  createSubscription,
  getCheckoutUrl,
  getSubscriptionUrl,
  validatePaymentLinkAccess,
} from "@/services/subscription.client";
import { Clock, Link as LinkIcon, Sparkles } from "lucide-react";
import { SubscriptionActionButton } from "@/components/subscription-action-button";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import SubscriptionPaymentMethodDialog from "@/components/subscription-payment-method-dialog";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

export default function SettingsSubscriptions() {
  useDocumentTitle("Subscriptions");
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const productParam = searchParams.get("type") as Product | null;
  const termParam = searchParams.get("term");
  const sessionId = searchParams.get("session_id");

  const { user } = useCurrentUser();
  const { organization } = useCurrentOrganization();
  const { members } = useMembers();
  const { memberships } = useMyMemberships();
  const { apiKeys } = useApiKeys();
  const { taxExemption } = useTaxExemption();
  const { subscriptions } = useSubscriptions(organization);
  const isAdmin = isOrgAdminOrOwner(organization, user, memberships);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isMatchLoading, setIsMatchLoading] = useState(false);
  const [isManageLoading, setIsManageLoading] = useState(false);
  const [isCancelLoading, setIsCancelLoading] = useState(false);
  const [isUpsellLoading, setIsUpsellLoading] = useState(false);
  const [isQuantityLoading, setIsQuantityLoading] = useState(false);
  const [isRenewLoading, setIsRenewLoading] = useState(false);
  const [isSubscribeLoading, setIsSubscribeLoading] = useState(false);
  const isBillingBusy =
    isManageLoading ||
    isCancelLoading ||
    isUpsellLoading ||
    isQuantityLoading ||
    isRenewLoading ||
    isSubscribeLoading;

  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const subscriptionPriceId = subscription?.priceId;
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("yearly");
  const [product, setProduct] = useState<Product>("poweron-pro");

  const calculatePercentage = (): string => {
    const paymentMethod = getPaymentMethodByPriceId(subscription!.priceId)!;
    const metadata = getSubscriptionMetadata(paymentMethod);

    switch (metadata?.term) {
      case "month":
        return "5%";
      case "year":
        return "10%";
      default:
        return "";
    }
  };

  const calculateSavings = (): string => {
    const currentPaymentMethod =
      getPaymentMethodByPriceId(subscription!.priceId) || paymentMethod;
    const metadata = getSubscriptionMetadata(currentPaymentMethod);
    const totalSeats = subscription?.seats || 1;

    let savings = 0;

    switch (true) {
      case metadata?.term === "month":
        savings =
          PaymentMethods.MONTHLY.powerOnProPrice * 12 * totalSeats -
          PaymentMethods.YEARLY.powerOnProPrice * totalSeats;
        break;
      case metadata?.term === "year" && product === "poweron-pro":
        savings =
          PaymentMethods.YEARLY.powerOnProPrice * 3 * totalSeats -
          PaymentMethods.THREE_YEAR.powerOnProPrice * totalSeats;
        break;
      case metadata?.term === "year" && product === "poweron-pipelines":
        savings =
          PaymentMethods.YEARLY.powerOnPipelinesPrice * 3 * totalSeats -
          PaymentMethods.THREE_YEAR.powerOnPipelinesPrice * totalSeats;
        break;
    }

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    })
      .format(savings)
      .slice(0, -3);
  };

  const handleCancelSubscription = async () => {
    if (organization === null || isBillingBusy) return;

    try {
      setIsCancelLoading(true);

      const url = await getSubscriptionUrl(
        organization.id,
        organization.customerId,
        subscription
          ? { type: "subscription_cancel", subscriptionId: subscription.id }
          : undefined,
      );

      if (url !== "" && subscription && subscription.canceledAt === null) {
        window.open(url, "_self");
      } else {
        toast.error(DEFAULT_ERROR_TITLE, {
          description:
            "We're unable to create a Stripe billing session. Please try again later.",
        });
      }
    } catch (error: any) {
      toast.error(
        ...overrideMessageIf403(error.message, [
          DEFAULT_ERROR_TITLE,
          {
            description:
              "There was a problem creating the Stripe billing session. Please try again later.",
          },
        ]),
      );
      console.warn(error.message);
    } finally {
      setIsCancelLoading(false);
    }
  };

  const handleManageSubscription = async (isUpsell = false) => {
    if (organization === null || isBillingBusy) return;

    // @TODO: For invoiced subscriptions, we need to support seat/hosts changes
    // within Portal and update the subscription in Stripe

    try {
      if (isUpsell) setIsUpsellLoading(true);
      else setIsManageLoading(true);

      const url = await getSubscriptionUrl(
        organization.id,
        organization.customerId,
      );

      if (url !== "") {
        window.open(url, "_self");
      } else {
        toast.error(DEFAULT_ERROR_TITLE, {
          description:
            "We're unable to create a Stripe billing session. Please try again later.",
        });
      }
    } catch (error: any) {
      toast.error(
        ...overrideMessageIf403(error.message, [
          DEFAULT_ERROR_TITLE,
          {
            description:
              "There was a problem creating the Stripe billing session. Please try again later.",
          },
        ]),
      );
      console.warn(error.message);
    } finally {
      setIsManageLoading(false);
      setIsUpsellLoading(false);
    }
  };

  const handleSubscribe = async (
    collectionMethod?: SubscriptionCollectionMethod,
    customer?: SubscriptionCustomer | string,
    quantity?: number,
    promotionCodeId?: string,
  ) => {
    if (organization === null || user === null) return;

    // Protect against non-admins and non-owners
    if (!isAdmin) {
      toast.error(DEFAULT_MISSING_PERMISSION_TITLE, {
        description:
          "You must be the organization owner or an admin to subscribe",
      });
      return;
    }

    if (isSubscribeLoading) return;
    setIsSubscribeLoading(true);
    try {
      // Monthly subscription (only PowerOn Pro+)
      if (paymentMethod === PaymentMethods.MONTHLY.type) {
        // Use checkout session for tax exempt customers or existing customers (to reuse payment methods)
        if (
          (taxExemption && taxExemption.status === "approved") ||
          (organization.customerId && organization.customerId !== "UNASSIGNED")
        ) {
          await handleTaxExemptSubscription();
          return;
        }

        try {
          await validatePaymentLinkAccess(organization.id);

          const baseUrl =
            process.env.NEXT_PUBLIC_STRIPE_POWERON_MONTHLY_PAYMENT_URL!;
          // client_reference_id propagates onto the payment link's checkout
          // session — the /api/stripe/sessions return-leg refuses to link the
          // session's customer to the org without it.
          const url = `${baseUrl}${baseUrl.includes("?") ? "&" : "?"}prefilled_email=${user.email}&client_reference_id=${organization.id}`;

          window.open(url, "_self");
        } catch (error: any) {
          toast.error(
            ...overrideMessageIf403(error.message, [
              DEFAULT_ERROR_TITLE,
              {
                description:
                  "There was a problem navigating to the Stripe payment link. Please try again later.",
              },
            ]),
          );
          console.warn(error.message);
        }
        return;
      }

      if (collectionMethod === SubscriptionCollectionMethod.AUTOMATIC) {
        // Use checkout session for tax exempt customers or existing customers (to reuse payment methods)
        if (
          (taxExemption && taxExemption.status === "approved") ||
          (organization.customerId && organization.customerId !== "UNASSIGNED")
        ) {
          await handleTaxExemptSubscription();
          return;
        }

        // Handle new customers via payment links
        try {
          await validatePaymentLinkAccess(organization.id);

          const metadata = getSubscriptionMetadata(paymentMethod);
          const baseUrl =
            product === "poweron-pro"
              ? metadata.powerOnProPaymentUrl!
              : metadata.powerOnPipelinesPaymentUrl!;
          // client_reference_id propagates onto the payment link's checkout
          // session — the /api/stripe/sessions return-leg refuses to link the
          // session's customer to the org without it.
          const url = `${baseUrl}${baseUrl.includes("?") ? "&" : "?"}prefilled_email=${user.email}&client_reference_id=${organization.id}`;

          window.open(url, "_self");
        } catch (error: any) {
          toast.error(
            ...overrideMessageIf403(error.message, [
              DEFAULT_ERROR_TITLE,
              {
                description:
                  "There was a problem navigating to the Stripe payment link. Please try again later.",
              },
            ]),
          );
          console.warn(error.message);
        }

        return;
      }

      if (collectionMethod === SubscriptionCollectionMethod.INVOICE) {
        try {
          const customerId =
            typeof customer === "string"
              ? customer
              : await createCustomer(organization.id, customer!);

          if (!customerId) {
            toast.error(DEFAULT_ERROR_TITLE, {
              description:
                "There was a problem creating the Stripe customer. Please try again later.",
            });
            setIsDialogOpen(false);
            return;
          }

          const metadata = getSubscriptionMetadata(paymentMethod);
          const priceId =
            product === "poweron-pro"
              ? metadata.powerOnProPriceId
              : metadata.powerOnPipelinesPriceId;

          const subscription = await createSubscription(
            organization.id,
            customerId,
            product,
            priceId!,
            quantity!,
            promotionCodeId,
          );

          if (!subscription) {
            toast.error(DEFAULT_ERROR_TITLE, {
              description:
                "There was a problem creating the Stripe subscription. Please try again later.",
            });
            setIsDialogOpen(false);
            return;
          }

          // createCustomer (-> /api/stripe/customers) writes customerId via
          // update_organization as caller, so the STDB org row picks it up
          // live; useSubscriptions itself filters org_subscriptions by
          // organization id + status === 'active', not customerId. customerId
          // is a server-owned field — the client no longer calls
          // updateOrganization for it directly (legacy did, here).
          setIsDialogOpen(false);
          toast.success("Subscription Created", {
            description: `Your subscription for ${getProductNameByProduct(product)} has been created. An invoice will be sent to your billing email shortly`,
          });
          return;
        } catch (error: any) {
          toast.error(
            ...overrideMessageIf403(error.message, [
              DEFAULT_ERROR_TITLE,
              {
                description:
                  "There was a problem creating the Stripe customer & subscription. Please try again later.",
              },
            ]),
          );
          setIsDialogOpen(false);
          console.warn(error.message);
        }
      }

      // Prompt for selecting a collection method
      setIsDialogOpen(true);
    } finally {
      setIsSubscribeLoading(false);
    }
  };

  const handleSubscriptionMatch = async () => {
    if (organization === null || user === null) return;

    // Protect against non-admins and non-owners
    if (!isAdmin) {
      toast.error(DEFAULT_MISSING_PERMISSION_TITLE, {
        description:
          "You must be the organization owner or an admin to match a subscription",
      });
      return;
    }

    try {
      setIsMatchLoading(true);

      const { count, activeProductIds } = await matchCustomerToOrganization(
        organization.id,
      );

      if (count === 1 && activeProductIds == null) {
        toast.warning("Unable to verify subscription", {
          description:
            "Your billing account was matched, but subscriptions could not be synced. Please try matching again.",
        });
      } else if (
        count === 1 &&
        activeProductIds?.includes(getSubscriptionProductIdByProduct(product))
      ) {
        toast.success("Subscription Updated", {
          description:
            "Your subscription has been matched with your organization successfully",
        });
      } else if (count > 1) {
        toast.warning("Multiple Billing Accounts Found", {
          description:
            "We found multiple billing accounts for your email. Please contact support to match the correct account.",
        });
      } else {
        toast.error("No Active Subscription Found", {
          description: `We couldn't find an active ${getProductNameByProduct(product)} subscription for ${user.email}.`,
        });
      }
    } catch (error: any) {
      toast.error(
        ...overrideMessageIf403(error.message, [
          DEFAULT_ERROR_TITLE,
          {
            description:
              "There was a problem matching your subscription. If this continues, please contact support",
          },
        ]),
      );
      console.warn(error.message);
    } finally {
      setIsMatchLoading(false);
    }
  };

  const handleTaxExemptSubscription = async () => {
    try {
      const metadata = getSubscriptionMetadata(paymentMethod);
      const priceId =
        product === "poweron-pro"
          ? metadata.powerOnProPriceId
          : metadata.powerOnPipelinesPriceId;

      const url = await getCheckoutUrl(
        organization!.id,
        organization!.customerId,
        product,
        priceId!,
      );
      if (url) {
        window.open(url, "_self");
      } else {
        toast.error(DEFAULT_ERROR_TITLE, {
          description:
            "There was a problem creating the Stripe checkout session. Please try again later.",
        });
      }
      return;
    } catch (error: any) {
      toast.error(
        ...overrideMessageIf403(error.message, [
          DEFAULT_ERROR_TITLE,
          {
            description:
              "There was a problem creating the Stripe checkout session. Please try again later.",
          },
        ]),
      );
      console.warn(error.message);
    }
  };

  const handleSubscriptionQuantity = async () => {
    if (organization === null || isBillingBusy) return;

    try {
      setIsQuantityLoading(true);
      const url = await getSubscriptionUrl(
        organization.id,
        organization.customerId,
        subscription
          ? { type: "subscription_update", subscriptionId: subscription.id }
          : undefined,
      );

      if (url !== "" && subscription) {
        window.open(url, "_self");
      } else {
        toast.error(DEFAULT_ERROR_TITLE, {
          description:
            "We're unable to create a Stripe billing session. Please try again later.",
        });
      }
    } catch (error: any) {
      toast.error(
        ...overrideMessageIf403(error.message, [
          DEFAULT_ERROR_TITLE,
          {
            description:
              "There was a problem creating the Stripe billing session. Please try again later.",
          },
        ]),
      );
      console.warn(error.message);
    } finally {
      setIsQuantityLoading(false);
    }
  };

  const handleRenewSubscription = async () => {
    if (organization === null || isBillingBusy) return;

    try {
      setIsRenewLoading(true);
      const url = await getSubscriptionUrl(
        organization.id,
        organization.customerId,
      );

      if (url !== "" && subscription && subscription.canceledAt !== null) {
        window.open(url, "_self");
      } else {
        toast.error(DEFAULT_ERROR_TITLE, {
          description:
            "We're unable to create a Stripe billing session. Please try again later.",
        });
      }
    } catch (error: any) {
      toast.error(
        ...overrideMessageIf403(error.message, [
          DEFAULT_ERROR_TITLE,
          {
            description:
              "There was a problem creating the Stripe billing session. Please try again later.",
          },
        ]),
      );
      console.warn(error.message);
    } finally {
      setIsRenewLoading(false);
    }
  };

  const debounceSubscriptionMatch = useDebounce(
    handleSubscriptionMatch,
    DEFAULT_DEBOUNCE_DELAY,
  );

  const confirmedSessionRef = useRef<string | null>(null);
  useEffect(() => {
    if (!sessionId || !organization) return;
    // One-shot per session id. `organization` is a LIVE object — the confirm
    // call below rewrites the org row server-side (update_organization bumps
    // updated_at even when customerId is unchanged), so the live view hands
    // back a new object identity and this effect re-runs; without the guard
    // that's a confirm → write → re-run loop for as long as session_id stays
    // in the URL.
    if (confirmedSessionRef.current === sessionId) return;
    confirmedSessionRef.current = sessionId;

    // The sessions GET syncs the session's customer onto the org SERVER-side
    // (update_organization as caller — customerId stays server-owned) and
    // relays the session's subscription into SpacetimeDB (first-subscribe
    // race heal). Both land in the live views, so no router.refresh() is
    // needed — instead, strip session_id from the URL: it cleans the address
    // bar and permanently disarms this effect.
    getCustomerFromSessionID(organization.id, sessionId)
      .catch((error: any) => console.warn(error.message))
      .finally(() => {
        const params = new URLSearchParams(searchParams.toString());
        params.delete("session_id");
        const query = params.toString();
        router.replace(query ? `${pathname}?${query}` : pathname, {
          scroll: false,
        });
      });
  }, [router, sessionId, pathname, searchParams, organization]);

  useEffect(() => {
    if (
      productParam &&
      (productParam === "poweron-pro" || productParam === "poweron-pipelines")
    ) {
      setProduct(productParam);
    } else {
      setProduct("poweron-pro");
    }
  }, [productParam]);

  useEffect(() => {
    setSubscription(
      subscriptions.find(
        (s) => s.productId === getSubscriptionProductIdByProduct(product),
      ) || null,
    );
  }, [subscriptions, product]);

  useEffect(() => {
    const requestedTerm =
      termParam === "monthly" ||
      termParam === "yearly" ||
      termParam === "three_year"
        ? termParam
        : null;
    const selectedTerm =
      requestedTerm ??
      (subscriptionPriceId
        ? getPaymentMethodByPriceId(subscriptionPriceId) || "yearly"
        : "yearly");
    setPaymentMethod(
      product === "poweron-pipelines" && selectedTerm === "monthly"
        ? "yearly"
        : selectedTerm,
    );
  }, [subscriptionPriceId, product, termParam]);

  const copySubscriptionLink = async () => {
    const url = new URL("/settings/subscriptions", window.location.origin);
    url.searchParams.set("type", product);
    url.searchParams.set("term", paymentMethod);
    try {
      await navigator.clipboard.writeText(url.toString());
      toast.success("Subscription link copied", {
        description:
          "Share this link to open the selected product and billing term.",
      });
    } catch {
      toast.error("Unable to copy subscription link", {
        description: "Please try copying the link again.",
      });
    }
  };

  return (
    <>
      <div>
        <h2 className="text-xl font-semibold leading-6 text-gray-1100 dark:text-gray-100">
          <button
            type="button"
            className="group flex w-fit cursor-pointer items-center gap-2 rounded-sm text-left"
            aria-label="Copy subscription link"
            title="Copy subscription link"
            onClick={copySubscriptionLink}
          >
            <span>
              Subscriptions<span className="text-muted">{" > "}</span>
              {getProductNameByProduct(product)}
            </span>
            <LinkIcon
              aria-hidden="true"
              className="size-4 shrink-0 -scale-x-100 text-muted opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
            />
          </button>
        </h2>
        <p className="mt-1">
          Manage your subscriptions, billing details, and invoices
        </p>
      </div>
      <div className="flex flex-col">
        <div className="mt-4 flex w-full flex-col items-start gap-3 lg:flex-row lg:items-center">
          <AnimatedTabs
            value={product}
            className="inline-flex w-full lg:mr-3 lg:w-[380px]"
            onValueChange={(value) => {
              if (productParam) {
                const params = new URLSearchParams(searchParams.toString());
                params.set("type", value);
                router.replace(`${pathname}?${params}`, { scroll: false });
              }
              setProduct(value as Product);
            }}
          >
            <AnimatedTabsList value={product}>
              <AnimatedTabsTrigger
                value="poweron-pro"
                className="text-xs sm:text-sm"
              >
                <PowerOnPro className="mr-1 mt-[1px] size-4 shrink-0 fill-foreground" />
                <span className="truncate">PowerOn Pro</span>
              </AnimatedTabsTrigger>
              <AnimatedTabsTrigger
                value="poweron-pipelines"
                className="text-xs sm:text-sm"
              >
                <PowerOnPipelines className="mr-1 mt-[1px] size-4 shrink-0" />
                <span className="truncate">PowerOn Pipelines</span>
              </AnimatedTabsTrigger>
            </AnimatedTabsList>
          </AnimatedTabs>
          {isAdmin && subscription && (
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center lg:ml-0">
              <SubscriptionActionButton
                loading={isManageLoading}
                disabled={isBillingBusy}
                wrapperClassName="w-full sm:w-auto"
                onClick={() => handleManageSubscription()}
                className="w-full text-xs sm:w-auto sm:text-sm"
              >
                <span className="sm:hidden">Billing & Invoices</span>
                <span className="hidden sm:inline">
                  Update Billing & View Invoices
                </span>
              </SubscriptionActionButton>
              {subscription.canceledAt === null && (
                <SubscriptionActionButton
                  loading={isCancelLoading}
                  disabled={isBillingBusy}
                  wrapperClassName="w-full sm:w-auto"
                  variant="destructive"
                  onClick={handleCancelSubscription}
                  className="w-full text-xs sm:w-auto sm:text-sm lg:ml-auto"
                >
                  Cancel
                </SubscriptionActionButton>
              )}
            </div>
          )}
        </div>
        <div className="mt-4 flex w-full flex-col items-start lg:flex-row lg:items-center">
          <AnimatedTabs
            value={paymentMethod}
            className="inline-flex w-full lg:mr-3 lg:w-[400px]"
            onValueChange={(value) => setPaymentMethod(value as PaymentMethod)}
          >
            <AnimatedTabsList value={paymentMethod}>
              <AnimatedTabsTrigger
                disabled={product === "poweron-pipelines"}
                value="monthly"
                className="px-2 text-xs sm:px-3 sm:text-sm"
              >
                Monthly
              </AnimatedTabsTrigger>
              <AnimatedTabsTrigger
                value="yearly"
                className="px-2 text-xs sm:px-3 sm:text-sm"
              >
                Yearly
                {product !== "poweron-pipelines" && (
                  <Badge
                    className="ml-1 gap-1 bg-emerald-500 text-emerald-50 sm:ml-4"
                    variant="largeTag"
                  >
                    <span className="hidden sm:inline">Save</span>5%
                  </Badge>
                )}
              </AnimatedTabsTrigger>
              <AnimatedTabsTrigger
                value="three_year"
                className="px-2 text-xs sm:px-3 sm:text-sm"
              >
                3-Year
                <Badge
                  className="ml-1 gap-1 bg-pink-400 text-pink-50 sm:ml-4"
                  variant="largeTag"
                >
                  <span className="hidden sm:inline">Save</span>10%
                </Badge>
              </AnimatedTabsTrigger>
            </AnimatedTabsList>
          </AnimatedTabs>
        </div>
      </div>
      {/* Renew subscription message */}
      {subscription && subscription.canceledAt !== null && (
        <div className="mt-3.5 sm:mt-5 md:mt-8">
          <Card>
            <CardHeader className="p-4 sm:p-8">
              <CardTitle className="text-base sm:text-lg">
                Renew your subscription
              </CardTitle>
              <CardDescription className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
                <div className="flex flex-wrap items-center gap-1 text-sm">
                  <span>Your</span>
                  {product === "poweron-pipelines" ? (
                    <span>PowerOn Pipelines</span>
                  ) : (
                    <span>
                      PowerOn Pro
                      <sup className="font-bold text-brand-accent">+</sup>
                    </span>
                  )}
                  <span>subscription expires on</span>
                  <span className="font-bold text-brand-accent">
                    {formatLongDate((subscription?.cancelAt || 0) + 86400)}
                  </span>
                  <Clock className="size-4" />
                </div>
                <SubscriptionActionButton
                  loading={isRenewLoading}
                  disabled={isBillingBusy}
                  wrapperClassName="w-full sm:w-auto"
                  variant="secondary"
                  onClick={handleRenewSubscription}
                  className="w-full text-sm sm:w-auto"
                >
                  <Sparkles className="size-4" />
                  Renew
                </SubscriptionActionButton>
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      )}
      {/* Upsell subscription message */}
      {isAdmin &&
        subscription &&
        subscription.cancelAt === null &&
        ![
          process.env.NEXT_PUBLIC_STRIPE_POWERON_THREE_YEAR_PRICE_ID,
          process.env.NEXT_PUBLIC_STRIPE_PIPELINES_THREE_YEAR_PRICE_ID,
        ].includes(subscription.priceId) && (
          <div className="mt-3.5 sm:mt-5 md:mt-8">
            <Card>
              <CardHeader className="p-4 sm:p-8">
                <CardTitle className="w-full text-base sm:text-lg">
                  Save {calculatePercentage()} from your subscription
                </CardTitle>
                <CardDescription className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
                  <div className="flex flex-wrap items-center gap-1 text-sm">
                    <span>Change billing to</span>
                    <span>
                      {getPaymentMethodByPriceId(subscription.priceId) ===
                      PaymentMethods.MONTHLY.type
                        ? "yearly"
                        : "every 3-years"}
                    </span>
                    <span>to save</span>
                    <span className="font-bold text-emerald-400">
                      {calculateSavings()}
                    </span>
                    {getPaymentMethodByPriceId(subscription.priceId) ===
                      PaymentMethods.MONTHLY.type && <span>a year</span>}
                    <span>on</span>
                    {product === "poweron-pipelines" ? (
                      <span>PowerOn Pipelines</span>
                    ) : (
                      <span>
                        PowerOn Pro
                        <sup className="font-bold text-brand-accent">+</sup>
                      </span>
                    )}
                  </div>
                  <SubscriptionActionButton
                    loading={isUpsellLoading}
                    disabled={isBillingBusy}
                    wrapperClassName="w-full sm:w-auto"
                    variant="secondary"
                    onClick={() => handleManageSubscription(true)}
                    className="w-full text-sm sm:w-auto"
                  >
                    <Sparkles className="size-4" />
                    <span className="sm:hidden">
                      {getPaymentMethodByPriceId(subscription.priceId) ===
                      PaymentMethods.MONTHLY.type
                        ? "Yearly"
                        : "3-Year"}{" "}
                      plan
                    </span>
                    <span className="hidden sm:inline">
                      Change to{" "}
                      {getPaymentMethodByPriceId(subscription.priceId) ===
                      PaymentMethods.MONTHLY.type
                        ? "yearly"
                        : "3-Year"}{" "}
                      plan
                    </span>
                  </SubscriptionActionButton>
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        )}
      <div className="mt-3.5 sm:mt-5 md:mt-8">
        {product === "poweron-pro" && (
          <SubscriptionPowerOnPro
            isAdmin={isAdmin}
            isMatchLoading={isMatchLoading}
            isSubscribeLoading={isSubscribeLoading}
            isQuantityLoading={isQuantityLoading}
            isBillingBusy={isBillingBusy}
            user={user}
            organization={organization}
            subscription={subscription}
            apiKeys={apiKeys}
            paymentMethod={paymentMethod}
            taxExemption={taxExemption}
            members={members}
            onMatch={debounceSubscriptionMatch}
            onQuantityChange={handleSubscriptionQuantity}
            onSubscribe={handleSubscribe}
          />
        )}
        {product === "poweron-pipelines" && (
          <SubscriptionPowerOnPipelines
            isAdmin={isAdmin}
            isMatchLoading={isMatchLoading}
            isSubscribeLoading={isSubscribeLoading}
            isQuantityLoading={isQuantityLoading}
            isBillingBusy={isBillingBusy}
            user={user}
            organization={organization}
            subscription={subscription}
            apiKeys={apiKeys}
            paymentMethod={paymentMethod}
            taxExemption={taxExemption}
            members={members}
            onMatch={debounceSubscriptionMatch}
            onQuantityChange={handleSubscriptionQuantity}
            onSubscribe={handleSubscribe}
          />
        )}
      </div>
      <MsgQuestion name="Subscriptions" />
      {/* Subscription payment method dialog */}
      <SubscriptionPaymentMethodDialog
        controlledOpen={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        onSuccess={(collectionMethod, customer, quantity, promotionCodeId) => {
          return handleSubscribe(
            collectionMethod,
            customer,
            quantity,
            promotionCodeId,
          );
        }}
        customerId={organization?.customerId || "UNASSIGNED"}
        product={product}
        subscriptionMetadata={getSubscriptionMetadata(paymentMethod)}
      />
    </>
  );
}
